import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
    Typography, Collapse, Link as MuiLink, Alert, CircularProgress, Grid,
} from '@mui/material';
import { createPart } from './partsApi';

// ---------------------------------------------------------------------------
// NewPartDialog — the "2-minute start" (design doc §5): one screen, three acts.
//   1. Part number (required) — name/revision tucked under "More options".
//   2. First feature — name + any subset of USL/LSL/nominal + unit.
//   3. A single composite POST /api/parts creates part + feature + the auto
//      default stream in one transaction/request. On success the caller
//      decides where to go next (Measure now / View part).
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144, 202, 249, 0.7)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144, 202, 249, 0.9)' },
};

/** Response shape is implementer judgment (recon endpointContracts §1) — accept
 * either a flat or a { part, feature, stream } nested envelope defensively. */
export function extractCompositeResult(data) {
    if (!data || typeof data !== 'object') return { part: null, feature: null, stream: null };
    const part = data.part || data;
    const feature = data.feature || (Array.isArray(part?.features) ? part.features[0] : null) || null;
    const stream = data.stream || (Array.isArray(feature?.streams) ? feature.streams[0] : null) || null;
    return { part, feature, stream };
}

function toNumberOrNull(s) {
    if (s === '' || s === null || s === undefined) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

const initialState = {
    partNumber: '',
    name: '',
    revision: '',
    featureName: '',
    usl: '',
    lsl: '',
    nominal: '',
    unit: '',
};

function NewPartDialog({ open, onClose, onCreated, onMeasureNow }) {
    const [fields, setFields] = useState(initialState);
    const [showMore, setShowMore] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [created, setCreated] = useState(null); // { part, feature, stream }

    const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));

    const reset = () => {
        setFields(initialState);
        setShowMore(false);
        setError(null);
        setCreated(null);
        setSubmitting(false);
    };

    const handleClose = () => {
        reset();
        onClose?.();
    };

    const handleSubmit = async () => {
        setError(null);
        const partNumber = fields.partNumber.trim();
        const featureName = fields.featureName.trim();
        if (!partNumber) {
            setError('Part number is required.');
            return;
        }
        if (!featureName) {
            setError('First feature needs a name.');
            return;
        }

        setSubmitting(true);
        try {
            const { ok, status, data } = await createPart({
                partNumber,
                name: fields.name.trim() || undefined,
                revision: fields.revision.trim() || undefined,
                feature: {
                    name: featureName,
                    usl: toNumberOrNull(fields.usl),
                    lsl: toNumberOrNull(fields.lsl),
                    nominal: toNumberOrNull(fields.nominal),
                    unit: fields.unit.trim() || null,
                },
            });
            if (!ok) {
                if (status === 409 || data?.code === 'CONFLICT') {
                    setError(`A part with number "${partNumber}"${fields.revision ? ` rev ${fields.revision}` : ''} already exists. Use "Rev bump" from the part instead, or pick a different revision.`);
                } else {
                    setError(data?.error || 'Could not create the part. Check the inputs and retry.');
                }
                return;
            }
            const result = extractCompositeResult(data);
            setCreated(result);
            onCreated?.(result);
        } catch (e) {
            setError('Network error. Check your connection and retry.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleMeasureNow = () => {
        onMeasureNow?.(created);
        reset();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}
        >
            <DialogTitle sx={{ color: 'rgba(255,255,255,0.9)' }}>New Part</DialogTitle>
            <DialogContent>
                {created ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
                        <Alert severity="success" sx={{ py: 0.5 }}>
                            {created.part?.part_number || fields.partNumber} created.
                        </Alert>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            Ready to record your first reading?
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                        <TextField
                            label="Part number"
                            required
                            autoFocus
                            size="small"
                            fullWidth
                            sx={inputSx}
                            value={fields.partNumber}
                            onChange={set('partNumber')}
                            disabled={submitting}
                            inputProps={{ maxLength: 120 }}
                        />

                        <MuiLink
                            component="button"
                            type="button"
                            onClick={() => setShowMore((v) => !v)}
                            underline="hover"
                            sx={{ color: '#90caf9', fontSize: '0.8rem', textAlign: 'left', width: 'fit-content' }}
                        >
                            {showMore ? 'Hide more options' : 'More options (name, revision)'}
                        </MuiLink>
                        <Collapse in={showMore}>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Name"
                                        size="small"
                                        fullWidth
                                        sx={inputSx}
                                        value={fields.name}
                                        onChange={set('name')}
                                        disabled={submitting}
                                        inputProps={{ maxLength: 200 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Revision"
                                        size="small"
                                        fullWidth
                                        sx={inputSx}
                                        value={fields.revision}
                                        onChange={set('revision')}
                                        disabled={submitting}
                                        inputProps={{ maxLength: 40 }}
                                    />
                                </Grid>
                            </Grid>
                        </Collapse>

                        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                            First feature
                        </Typography>
                        <TextField
                            label="Feature name"
                            required
                            size="small"
                            fullWidth
                            sx={inputSx}
                            placeholder='e.g. "OD Ø12.700"'
                            value={fields.featureName}
                            onChange={set('featureName')}
                            disabled={submitting}
                            inputProps={{ maxLength: 160 }}
                        />
                        <Grid container spacing={1.5}>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    label="LSL" size="small" fullWidth sx={inputSx} type="number"
                                    value={fields.lsl} onChange={set('lsl')} disabled={submitting}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    label="Nominal" size="small" fullWidth sx={inputSx} type="number"
                                    value={fields.nominal} onChange={set('nominal')} disabled={submitting}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    label="USL" size="small" fullWidth sx={inputSx} type="number"
                                    value={fields.usl} onChange={set('usl')} disabled={submitting}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    label="Unit" size="small" fullWidth sx={inputSx}
                                    value={fields.unit} onChange={set('unit')} disabled={submitting}
                                    inputProps={{ maxLength: 24 }}
                                />
                            </Grid>
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ py: 0.5 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {created ? (
                    <>
                        <Button onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.6)' }}>Done</Button>
                        <Button variant="contained" color="primary" onClick={handleMeasureNow}>
                            Measure now
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                        >
                            {submitting ? 'Creating…' : 'Create part'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default NewPartDialog;
